import React from 'react';
import { theme } from '../../../theme';
import { ChevronLeft, ChevronRight, Calendar, LayoutGrid, List } from 'lucide-react';

interface BookingGridControlsProps {
  date: string;
  view: 'daily' | 'weekly';
  venues: { id: string; name: string }[];
  selectedVenueId?: string;
  onDateChange: (date: string) => void;
  onViewChange: (view: 'daily' | 'weekly') => void;
  onVenueChange: (venueId: string | undefined) => void;
  onGoToToday: () => void;
  onGoToPrevious: () => void;
  onGoToNext: () => void;
}

const BookingGridControls: React.FC<BookingGridControlsProps> = ({
  date,
  view,
  venues,
  selectedVenueId,
  onDateChange,
  onViewChange,
  onVenueChange,
  onGoToToday,
  onGoToPrevious,
  onGoToNext,
}) => {
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '16px',
  };

  const leftControlsStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const rightControlsStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 16px',
    borderRadius: theme.borderRadius.button,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: 'transparent',
    color: theme.colors.text,
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    gap: '4px',
  };

  const iconButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    padding: '8px',
  };

  const activeButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: theme.colors.primary,
    color: theme.colors.background,
    borderColor: theme.colors.primary,
  };

  const dateDisplayStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 16px',
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.button,
    border: `1px solid ${theme.colors.border}`,
  };

  const dateTextStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    minWidth: '200px',
    textAlign: 'center',
  };

  const selectStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: theme.borderRadius.button,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontSize: theme.fonts.sizeSmall,
    cursor: 'pointer',
    minWidth: '150px',
  };

  const formatDate = (dateStr: string) => {
    const dateObj = new Date(dateStr + 'T00:00:00');
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return dateObj.toLocaleDateString('es-AR', options);
  };

  const formatWeekRange = (dateStr: string) => {
    const dateObj = new Date(dateStr + 'T00:00:00');
    const day = dateObj.getDay();
    const diff = dateObj.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(dateObj.setDate(diff));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    return `${monday.toLocaleDateString('es-AR', options)} - ${sunday.toLocaleDateString('es-AR', options)}`;
  };

  return (
    <div style={containerStyle}>
      <div style={leftControlsStyle}>
        <button
          style={iconButtonStyle}
          onClick={onGoToPrevious}
          title={view === 'weekly' ? 'Semana anterior' : 'Día anterior'}
        >
          <ChevronLeft size={20} />
        </button>

        <div style={dateDisplayStyle}>
          <Calendar size={18} color={theme.colors.primary} />
          <span style={dateTextStyle}>
            {view === 'weekly' ? formatWeekRange(date) : formatDate(date)}
          </span>
        </div>

        <button
          style={iconButtonStyle}
          onClick={onGoToNext}
          title={view === 'weekly' ? 'Semana siguiente' : 'Día siguiente'}
        >
          <ChevronRight size={20} />
        </button>

        <button style={buttonStyle} onClick={onGoToToday}>
          Hoy
        </button>

        <input
          type="date"
          value={date}
          onChange={(e) => onDateChange(e.target.value)}
          style={{
            ...selectStyle,
            colorScheme: 'dark',
          }}
        />
      </div>

      <div style={rightControlsStyle}>
        {venues.length > 1 && (
          <select
            value={selectedVenueId || ''}
            onChange={(e) => onVenueChange(e.target.value || undefined)}
            style={selectStyle}
          >
            {view !== 'weekly' && <option value="">Todas las canchas</option>}
            {venues.map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
              </option>
            ))}
          </select>
        )}

        <div style={{ display: 'flex', gap: '4px' }}>
          <button
            style={view === 'daily' ? activeButtonStyle : buttonStyle}
            onClick={() => onViewChange('daily')}
            title="Vista diaria"
          >
            <LayoutGrid size={18} />
            Diaria
          </button>
          <button
            style={view === 'weekly' ? activeButtonStyle : buttonStyle}
            onClick={() => onViewChange('weekly')}
            title="Vista semanal"
          >
            <List size={18} />
            Semanal
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingGridControls;
